/**
 * Indicative cost ranges (USD) for common project types in Zimbabwe.
 *
 * Shown in the project wizard's Budget step so a first-time owner has an
 * anchor before quotes arrive. Ranges are deliberately wide — labour,
 * materials, and site conditions vary a lot — and every display must carry
 * the "actual quotes may differ" note. Keys are matched as substrings
 * against the wizard's task/category/full-project selections (longest key
 * wins), so composite strings like "Renovation: Kitchen Renovation" resolve
 * to the most specific entry.
 */

const COST_RANGES = {
  // Quick tasks — specific
  'Interior Painting': [300, 1500],
  'Exterior Painting': [500, 2500],
  'Roof Painting': [400, 1500],
  'Wall Painting': [200, 800],
  'Pipe Repair': [50, 300],
  'Tap Installation': [30, 150],
  'Toilet Repair': [80, 350],
  'Water Heater': [350, 900],
  'Drainage': [150, 800],
  'Wiring': [800, 3000],
  'Socket Installation': [20, 80],
  'Light Fixtures': [30, 150],
  'Electrical Repair': [50, 300],
  'Roof Repair': [150, 800],
  'Roof Replacement': [2500, 9000],
  'Gutter Installation': [200, 900],
  'Leak Repair': [100, 500],
  'Wall Construction': [1500, 6000],
  'Wall Repair': [100, 600],
  'Plastering': [300, 1500],
  'Tiling': [400, 2500],
  'Door Installation': [80, 300],
  'Window Installation': [100, 400],
  'Door Repair': [40, 200],
  'Window Repair': [40, 200],
  'Landscaping': [300, 2000],
  'Paving': [500, 3000],
  'Fence Construction': [800, 4000],
  'Gate Installation': [300, 1500],
  'General Maintenance': [100, 600],
  'Inspection': [50, 200],
  'Cleaning': [80, 400],

  // Quick tasks — category fallbacks
  'Painting': [200, 2500],
  'Plumbing': [30, 900],
  'Electrical': [20, 3000],
  'Roofing': [100, 9000],
  'Walls': [100, 6000],
  'Doors/Windows': [40, 400],
  'Outdoor': [300, 4000],
  'Maintenance': [50, 600],

  // Full projects — specific
  '3-Bedroom House': [35000, 70000],
  '4-Bedroom House': [50000, 95000],
  'Cottage': [15000, 35000],
  'Apartment Building': [120000, 400000],
  'Full House Renovation': [8000, 30000],
  'Kitchen Renovation': [3000, 12000],
  'Bathroom Renovation': [1500, 6000],
  'Room Addition': [5000, 15000],
  'Modernization': [10000, 40000],
  'Extension': [8000, 25000],
  'Conversion': [6000, 20000],
  'Office Building': [80000, 300000],
  'Retail Space': [40000, 150000],
  'Warehouse': [60000, 250000],
  'Restaurant': [50000, 180000],

  // Full projects — category fallbacks
  'New Construction': [15000, 95000],
  'Renovation': [1500, 30000],
  'Complete Upgrade': [6000, 40000],
  'Commercial': [40000, 300000]
};

/**
 * Look up the most specific indicative range for the wizard's current
 * selections. Returns { low, high, matchedOn } or null when nothing matches
 * (e.g. Custom tasks).
 */
export function getCostGuide({ taskCategory, specificTask, fullProjectType } = {}) {
  // Match the most specific selection first — a specific task ("Wiring")
  // must never lose to its broader category ("Electrical") just because the
  // category name is longer. Within one field, the longest key still wins
  // (composite full-project strings contain both category and subtype).
  const fields = [specificTask, fullProjectType, taskCategory].filter(Boolean);
  for (const field of fields) {
    let best = null;
    for (const [key, [low, high]] of Object.entries(COST_RANGES)) {
      if (field.includes(key)) {
        if (!best || key.length > best.matchedOn.length) {
          best = { low, high, matchedOn: key };
        }
      }
    }
    if (best) return best;
  }
  return null;
}

export function formatRange({ low, high }) {
  const fmt = (n) => `$${n.toLocaleString()}`;
  return `${fmt(low)} – ${fmt(high)}`;
}
